import { useState, useEffect } from 'react';
import { CheckCircle2, Circle } from 'lucide-react';

interface PostOnboardingRitualProps {
  onComplete: () => void;
}

export default function PostOnboardingRitual({ onComplete }: PostOnboardingRitualProps) {
  const [currentStep, setCurrentStep] = useState<'phrase' | 'commitment'>('phrase');
  const [isChecked, setIsChecked] = useState(false);
  const [showButton, setShowButton] = useState(false);

  // Cores do ShapeX

  const BORDER_COLOR = 'rgba(255, 255, 255, 0.1)';
  const BG_DARK = '#0D0D0D';
  const CARD_BG = '#141414';

  // Etapa 1: Frase cinematográfica - 2 segundos
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentStep('commitment');
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Etapa 3: Revelação do botão após marcar checkbox
  useEffect(() => {
    if (isChecked) {
      const timer = setTimeout(() => {
        setShowButton(true);
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setShowButton(false);
    }
  }, [isChecked]);

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: BG_DARK }}>
      {/* Etapa 1 - Frase Cinematográfica */}
      {currentStep === 'phrase' && (
        <div
          className="text-center px-6"
          style={{
            animation: 'fadeInOut 2s ease-in-out forwards'
          }}
        >
          <h1
            className="text-2xl font-extrabold text-white"
            style={{
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontWeight: 800,
              lineHeight: 1.3
            }}
          >
            Agora é só você, o escuro e a disciplina.
          </h1>
        </div>
      )}

      {/* Etapa 2 & 3 - Card do Compromisso */}
      {currentStep === 'commitment' && (
        <div
          className="w-full max-w-md mx-auto px-4"
          style={{
            animation: 'fadeIn 0.6s ease-out'
          }}
        >
          {/* Card principal */}
          <div
            className="rounded-[18px] p-8 border shadow-lg"
            style={{
              backgroundColor: CARD_BG,
              borderColor: BORDER_COLOR,
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)'
            }}
          >
            {/* Texto superior */}
            <p
              className="text-center mb-8 text-[#A1A1A1] text-sm font-medium"
              style={{
                fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}
            >
              Marque a caixa abaixo para iniciar sua jornada
            </p>

            {/* Checkbox e Compromisso */}
            <div
              className="flex items-center gap-4 mb-0 cursor-pointer group"
              onClick={handleCheckboxChange}
            >
              <div className="relative">
                {isChecked ? (
                  <div className="bg-[#E53935]/10 rounded-full p-0.5">
                    <CheckCircle2
                      className="w-7 h-7 text-[#E53935]"
                      style={{ animation: 'bounceSubtle 0.1s ease-out' }}
                    />
                  </div>
                ) : (
                  <Circle
                    className="w-7 h-7 text-[#A1A1A1] group-hover:text-[#E53935] transition-colors"
                  />
                )}
              </div>
              <label
                className="cursor-pointer flex-1 text-lg font-bold text-white group-hover:text-white/90 transition-colors"
                style={{
                  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  lineHeight: 1.2,
                }}
              >
                Eu não negocio com a desistência.
              </label>
            </div>
          </div>

          {/* Botão - aparece apenas após marcar */}
          {showButton && (
            <div
              className="mt-6"
              style={{
                animation: 'fadeIn 0.6s ease-out'
              }}
            >
              <button
                type="button"
                onClick={onComplete}
                className="w-full px-4 py-4 text-sm font-bold rounded-xl border transition-all duration-200 hover:scale-[1.02] shadow-lg shadow-red-900/20"
                style={{
                  backgroundColor: '#E53935',
                  borderColor: '#E53935',
                  color: 'white',
                  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}
              >
                Entrar para o Clube
              </button>
            </div>
          )}
        </div>
      )}

      {/* Estilos de animação inline */}
      <style>{`
        @keyframes fadeInOut {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          20% {
            opacity: 1;
            transform: translateY(0);
          }
          80% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(-20px);
          }
        }

        @keyframes fadeIn {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bounceSubtle {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-3px);
          }
        }
      `}</style>
    </div>
  );
}